import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomCalendar from '../components/CustomCalendar';
import CustomTimePicker from '../components/CustomTimePicker';
import { createTransferBooking, createPaymentCheckout } from '../services/api';
import { 
  ChevronDown, CheckCircle2, Loader2, 
  Users, Calendar, Phone, Mail, User, 
  MessageSquare, Baby, CreditCard, Lock, Plane,
  ChevronLeft, MapPin, Clock, Car, Check, ArrowLeftRight, Info
} from 'lucide-react';

// Region colors
const REGION_COLORS = {
  'Messinia': 'bg-emerald-100 text-emerald-700',
  'Laconia': 'bg-purple-100 text-purple-700',
  'Arcadia': 'bg-blue-100 text-blue-700',
  'Argolida': 'bg-amber-100 text-amber-700',
  'Corinthia': 'bg-rose-100 text-rose-700',
  'Achaia': 'bg-cyan-100 text-cyan-700',
  'Ilia': 'bg-orange-100 text-orange-700',
};

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data passed from RouteDetailPage
  const { route, vehicle, region } = location.state || {};
  
  // Redirect if no data
  useEffect(() => {
    if (!route || !vehicle) {
      navigate('/routes');
    }
  }, [route, vehicle, navigate]);

  // Booking form state
  const [passengers, setPassengers] = useState(2);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('');
  const [babySeat, setBabySeat] = useState(0);
  const [childSeat, setChildSeat] = useState(0);
  const [paymentType, setPaymentType] = useState('full');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  
  // Return trip state
  const [addReturnTrip, setAddReturnTrip] = useState(false);
  const [returnDate, setReturnDate] = useState(null);
  const [returnTime, setReturnTime] = useState('');
  const [returnFlightNo, setReturnFlightNo] = useState('');
  
  // Return trip dropdowns
  const [showReturnCalendar, setShowReturnCalendar] = useState(false);
  const [showReturnTimePicker, setShowReturnTimePicker] = useState(false);
  
  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    flightNo: '',
    pickupAddress: '',
    dropoffAddress: '',
    specialRequests: ''
  });

  // Restore form data from sessionStorage (for browser back from Stripe)
  useEffect(() => {
    const savedData = sessionStorage.getItem('checkoutFormData');
    if (savedData && route && vehicle) {
      try {
        const parsed = JSON.parse(savedData);
        // Only restore if same route and vehicle
        if (parsed.routeKey === `${route.from_slug}-${route.to_slug}-${vehicle.id}`) {
          setFormData(parsed.formData || {});
          setPassengers(parsed.passengers || 2);
          setBabySeat(parsed.babySeat || 0);
          setChildSeat(parsed.childSeat || 0);
          setPaymentType(parsed.paymentType || 'full');
          setAddReturnTrip(parsed.addReturnTrip || false);
          setReturnTime(parsed.returnTime || '');
          setReturnFlightNo(parsed.returnFlightNo || '');
          setPickupTime(parsed.pickupTime || '');
          
          // Restore dates (need to convert from ISO string back to Date)
          if (parsed.pickupDate) {
            setPickupDate(new Date(parsed.pickupDate));
          }
          if (parsed.returnDate) {
            setReturnDate(new Date(parsed.returnDate));
          }
          
          // Clear saved data after restoring
          sessionStorage.removeItem('checkoutFormData');
        }
      } catch (e) {
        console.error('Error restoring form data:', e);
      }
    }
  }, [route, vehicle]);

  // Function to save form data to sessionStorage
  const saveFormDataToSession = () => {
    if (route && vehicle) {
      const dataToSave = {
        routeKey: `${route.from_slug}-${route.to_slug}-${vehicle.id}`,
        formData,
        passengers,
        babySeat,
        childSeat,
        paymentType,
        addReturnTrip,
        returnTime,
        returnFlightNo,
        pickupTime,
        pickupDate: pickupDate?.toISOString() || null,
        returnDate: returnDate?.toISOString() || null,
      };
      sessionStorage.setItem('checkoutFormData', JSON.stringify(dataToSave));
    }
  };

  // Validation errors
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  
  // Determine which fields to show based on route
  const isFromAirport = route?.from_slug?.includes('airport') || false;
  const isToAirport = route?.to_slug?.includes('airport') || false;
  
  // Return trip is FROM airport when original trip is TO airport
  const returnIsFromAirport = isToAirport;
  
  // Dropdowns state
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showBabyDropdown, setShowBabyDropdown] = useState(false);
  const [showChildDropdown, setShowChildDropdown] = useState(false);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = () => {
      setShowPassengerDropdown(false);
      setShowCalendar(false);
      setShowTimePicker(false);
      setShowBabyDropdown(false);
      setShowChildDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Format helpers
  const formatDateDisplay = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTimeDisplay = (time) => {
    if (!time) return 'Select time';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    // Use local date components to avoid timezone shift issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Pricing
  const basePrice = vehicle?.price || 0;
  const returnPrice = addReturnTrip ? basePrice : 0;
  const totalPrice = basePrice + returnPrice;
  const depositAmount = Math.round(totalPrice * 0.25);
  const payNowAmount = paymentType === 'deposit' ? depositAmount : totalPrice;

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (showErrors && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Date & Time
    if (!pickupDate) newErrors.date = 'Please select a date';
    if (!pickupTime) newErrors.time = 'Please select a time';
    
    // Contact fields
    if (!formData.name.trim()) newErrors.name = 'Please enter your full name';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.phone.trim()) newErrors.phone = 'Please enter your phone number';
    
    // Flight number (only if from airport)
    if (isFromAirport && !formData.flightNo.trim()) {
      newErrors.flightNo = 'Please enter your flight number';
    }
    
    // Pickup address (only if not from airport)
    if (!isFromAirport && !formData.pickupAddress.trim()) {
      newErrors.pickupAddress = 'Please enter the pickup address';
    }
    
    // Drop off address (only if not to airport)
    if (!isToAirport && !formData.dropoffAddress.trim()) {
      newErrors.dropoffAddress = 'Please enter the drop off address';
    }
    
    // Return trip validation
    if (addReturnTrip) {
      if (!returnDate) {
        newErrors.returnDate = 'Please select a return date';
      } else if (pickupDate && returnDate <= pickupDate) {
        newErrors.returnDate = 'Return date must be after pickup date';
      }
      if (!returnTime) newErrors.returnTime = 'Please select a return time';
      // Return flight number only if return is from airport
      if (returnIsFromAirport && !returnFlightNo.trim()) {
        newErrors.returnFlightNo = 'Please enter your return flight number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Scroll to first error field
  const scrollToFirstError = (errorKeys) => {
    const fieldOrder = ['date', 'time', 'name', 'email', 'phone', 'flightNo', 'pickupAddress', 'dropoffAddress', 'returnDate', 'returnTime', 'returnFlightNo'];
    
    for (const field of fieldOrder) {
      if (errorKeys.includes(field)) {
        const element = document.querySelector(`[data-field="${field}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
  };

  // Handle booking submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    
    if (!validateForm()) {
      // Get error keys and scroll to first error
      const newErrors = {};
      if (!pickupDate) newErrors.date = true;
      if (!pickupTime) newErrors.time = true;
      if (!formData.name.trim()) newErrors.name = true;
      if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = true;
      if (!formData.phone.trim()) newErrors.phone = true;
      if (isFromAirport && !formData.flightNo.trim()) newErrors.flightNo = true;
      if (!isFromAirport && !formData.pickupAddress.trim()) newErrors.pickupAddress = true;
      if (!isToAirport && !formData.dropoffAddress.trim()) newErrors.dropoffAddress = true;
      if (addReturnTrip) {
        if (!returnDate) newErrors.returnDate = true;
        if (!returnTime) newErrors.returnTime = true;
        if (returnIsFromAirport && !returnFlightNo.trim()) newErrors.returnFlightNo = true;
      }
      
      setTimeout(() => scrollToFirstError(Object.keys(newErrors)), 100);
      return;
    }

    setIsSubmitting(true);
    setBookingError(null);

    try {
      const vehicleIdMap = {
        1: 'standard-car',
        2: 'executive-car',
        3: 'standard-van',
        4: 'executive-van'
      };

      const bookingData = {
        from_slug: route.from_slug,
        to_slug: route.to_slug,
        vehicle_id: vehicle.id ? vehicleIdMap[vehicle.id] || vehicle.id : 'standard-car',
        passengers: passengers,
        pickup_date: formatDateForApi(pickupDate),
        pickup_time: pickupTime,
        flight_number: formData.flightNo || null,
        pickup_address: formData.pickupAddress || null,
        dropoff_address: formData.dropoffAddress || null,
        baby_seats: babySeat,
        child_seats: childSeat,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        special_requests: formData.specialRequests || null,
        payment_type: paymentType,
        // Return trip data
        has_return_trip: addReturnTrip,
        return_date: addReturnTrip ? formatDateForApi(returnDate) : null,
        return_time: addReturnTrip ? returnTime : null,
        return_flight_number: addReturnTrip && returnIsFromAirport ? returnFlightNo : null
      };

      const result = await createTransferBooking(bookingData);
      const paymentResponse = await createPaymentCheckout(result.booking_id, paymentType === 'deposit' ? 'deposit' : 'full');
      
      if (paymentResponse.url) {
        // Save form data before redirecting to Stripe
        saveFormDataToSession();
        window.location.href = paymentResponse.url;
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setBookingError(err.message || 'Failed to create booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Don't render if no data
  // Set document meta tags - must be called before early return
  useDocumentMeta({
    title: route && vehicle ? `Checkout - ${route.from_name} to ${route.to_name} | Kalamata Airport Taxi` : 'Checkout | Kalamata Airport Taxi',
    description: route && vehicle ? `Complete your booking for transfer from ${route.from_name} to ${route.to_name}` : 'Complete your transfer booking'
  });

  if (!route || !vehicle) {
    return null;
  }

  const regionColor = REGION_COLORS[region] || REGION_COLORS['Messinia'];

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-[#2d232e] pt-28 pb-8">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/5 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Back Link */}
          <Link
            to={`/routes/${route.from_slug}/${route.to_slug}`}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to route</span>
          </Link>

          {/* Region Badge */}
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${regionColor}`}>
              {region || 'Messinia'}
            </span>
          </div>

          {/* Route Direction with Vertical Line */}
          <div className="flex gap-5 mb-6">
            <div className="flex flex-col items-center py-1">
              <div className="w-4 h-4 rounded-full bg-[#36827f]"></div>
              <div className="w-0.5 flex-1 bg-[#36827f]/40 my-2 min-h-[40px]"></div>
              <div className="w-4 h-4 rounded-full bg-[#36827f]/50"></div>
            </div>
            
            <div className="flex flex-col justify-between">
              <div className="pb-3">
                <span className="text-white/50 text-sm">From</span>
                <h1 className="text-2xl md:text-3xl font-bold text-white">{route.from_name}</h1>
              </div>
              <div className="pt-3">
                <span className="text-white/50 text-sm">To</span>
                <h2 className="text-2xl md:text-3xl font-bold text-[#36827f]">{route.to_name}</h2>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap items-center gap-6 text-white/80 mb-6">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#36827f]" />
              <span>{route.distance}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#36827f]" />
              <span>{route.duration}</span>
            </div>
          </div>

          {/* Selected Vehicle */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#36827f]/20 flex items-center justify-center">
              <Car className="h-5 w-5 text-[#36827f]" />
            </div>
            <div>
              <span className="text-white/50 text-sm">Selected Vehicle</span>
              <p className="text-white font-semibold">{vehicle.type}</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-white/10">
            {/* Step 1 - Complete */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#36827f] flex items-center justify-center">
                <Check className="h-4 w-4 text-white" />
              </div>
              <span className="text-white font-medium hidden sm:inline">Select Vehicle</span>
            </div>
            
            <div className="flex-1 h-0.5 bg-[#36827f] max-w-[60px]"></div>
            
            {/* Step 2 - Current */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#36827f] flex items-center justify-center ring-4 ring-[#36827f]/30">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <span className="text-white font-medium hidden sm:inline">Booking Details</span>
            </div>
            
            <div className="flex-1 h-0.5 bg-white/20 max-w-[60px]"></div>
            
            {/* Step 3 - Pending */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white/60 font-bold text-sm">3</span>
              </div>
              <span className="text-white/60 font-medium hidden sm:inline">Payment</span>
            </div>
          </div>
        </div>
      </section>
      
      <main className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Form - Left Side */}
              <div className="lg:col-span-2">
                {/* Booking Details */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-[#36827f]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Booking Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Passengers */}
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Users className="h-4 w-4 inline mr-1 text-[#36827f]" /> 
                        Passengers <span className="text-xs text-gray-400">(max {vehicle?.passengers || 3})</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowPassengerDropdown(!showPassengerDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                      >
                        <span>{passengers} {passengers === 1 ? 'passenger' : 'passengers'}</span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showPassengerDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      {showPassengerDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-30 max-h-[200px] overflow-y-auto">
                          {Array.from({ length: vehicle?.passengers || 3 }, (_, i) => i + 1).map(num => (
                            <div
                              key={num}
                              className={`px-4 py-3 cursor-pointer transition-colors ${passengers === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                              onClick={() => { setPassengers(num); setShowPassengerDropdown(false); }}
                            >
                              {num} {num === 1 ? 'passenger' : 'passengers'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Date */}
                    <div className="relative" data-field="date" onClick={(e) => e.stopPropagation()}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="h-4 w-4 inline mr-1 text-[#36827f]" /> Date
                      </label>
                      <button
                        type="button"
                        onClick={() => { setShowCalendar(!showCalendar); setShowTimePicker(false); if (errors.date) setErrors(prev => ({...prev, date: null})); }}
                        className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showErrors && errors.date ? 'border-red-500' : 'border-gray-200'}`}
                      >
                        <span className={!pickupDate ? 'text-gray-400' : ''}>{formatDateDisplay(pickupDate)}</span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCalendar ? 'rotate-180' : ''}`} />
                      </button>
                      {showErrors && errors.date && (
                        <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                      )}
                      {showCalendar && (
                        <div className="absolute top-full left-0 right-0 mt-2 z-30">
                          <CustomCalendar 
                            value={pickupDate}
                            onChange={(date) => { setPickupDate(date); setErrors(prev => ({...prev, date: null})); }}
                            onClose={() => setShowCalendar(false)}
                          />
                        </div>
                      )}
                    </div>

                    {/* Time */}
                    <div className="relative" data-field="time" onClick={(e) => e.stopPropagation()}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="h-4 w-4 inline mr-1 text-[#36827f]" /> Time
                      </label>
                      <button
                        type="button"
                        onClick={() => { setShowTimePicker(!showTimePicker); setShowCalendar(false); if (errors.time) setErrors(prev => ({...prev, time: null})); }}
                        className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showErrors && errors.time ? 'border-red-500' : 'border-gray-200'}`}
                      >
                        <span className={!pickupTime ? 'text-gray-400' : ''}>{formatTimeDisplay(pickupTime)}</span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTimePicker ? 'rotate-180' : ''}`} />
                      </button>
                      {showErrors && errors.time && (
                        <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                      )}
                      {showTimePicker && (
                        <div className="absolute top-full left-0 right-0 mt-2 z-30">
                          <CustomTimePicker 
                            value={pickupTime}
                            onChange={(time) => { setPickupTime(time); setErrors(prev => ({...prev, time: null})); }}
                            onClose={() => setShowTimePicker(false)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div data-field="name">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <User className="h-4 w-4 inline mr-1 text-[#36827f]" /> Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Your full name"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.name ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {showErrors && errors.name && (
                        <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>
                    <div data-field="email">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="h-4 w-4 inline mr-1 text-[#36827f]" /> Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.email ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {showErrors && errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    <div data-field="phone">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="h-4 w-4 inline mr-1 text-[#36827f]" /> Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+30 123 456 7890"
                        className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {showErrors && errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Flight Number & Drop off Address Row */}
                  {(isFromAirport || !isToAirport) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Flight Number - Show only when FROM is airport */}
                      {isFromAirport && (
                        <div data-field="flightNo">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Plane className="h-4 w-4 inline mr-1 text-[#36827f]" /> Flight Number
                          </label>
                          <input
                            type="text"
                            name="flightNo"
                            value={formData.flightNo}
                            onChange={handleInputChange}
                            placeholder="e.g., BA1234"
                            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.flightNo ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {showErrors && errors.flightNo && (
                            <p className="text-red-500 text-sm mt-1">{errors.flightNo}</p>
                          )}
                        </div>
                      )}

                      {/* Drop off Address - Show only when TO is NOT airport */}
                      {!isToAirport && (
                        <div data-field="dropoffAddress">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <MapPin className="h-4 w-4 inline mr-1 text-[#36827f]" /> Drop off Address
                          </label>
                          <input
                            type="text"
                            name="dropoffAddress"
                            value={formData.dropoffAddress}
                            onChange={handleInputChange}
                            placeholder="Enter your drop off address"
                            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.dropoffAddress ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {showErrors && errors.dropoffAddress && (
                            <p className="text-red-500 text-sm mt-1">{errors.dropoffAddress}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pickup Address - Show only when FROM is NOT airport */}
                  {!isFromAirport && (
                    <div className="mb-6" data-field="pickupAddress">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <MapPin className="h-4 w-4 inline mr-1 text-[#36827f]" /> Pickup Address
                      </label>
                      <input
                        type="text"
                        name="pickupAddress"
                        value={formData.pickupAddress}
                        onChange={handleInputChange}
                        placeholder="Enter your pickup address"
                        className={`w-full md:w-1/2 px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.pickupAddress ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {showErrors && errors.pickupAddress && (
                        <p className="text-red-500 text-sm mt-1">{errors.pickupAddress}</p>
                      )}
                    </div>
                  )}

                  {/* Child Seats */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <Baby className="h-4 w-4 inline mr-1 text-[#36827f]" /> Child Seats <span className="text-[#36827f] text-xs font-normal">(Free)</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Baby Seat */}
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <p className="text-xs text-gray-500 mb-2">Baby (0-1yr)</p>
                        <button
                          type="button"
                          onClick={() => setShowBabyDropdown(!showBabyDropdown)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                        >
                          <span>{babySeat}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showBabyDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showBabyDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-30">
                            {[0, 1, 2, 3].map(num => (
                              <div
                                key={num}
                                className={`px-4 py-3 cursor-pointer transition-colors ${babySeat === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                onClick={() => { setBabySeat(num); setShowBabyDropdown(false); }}
                              >
                                {num}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Child Seat */}
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <p className="text-xs text-gray-500 mb-2">Child (1-7yr)</p>
                        <button
                          type="button"
                          onClick={() => setShowChildDropdown(!showChildDropdown)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                        >
                          <span>{childSeat}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showChildDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showChildDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-30">
                            {[0, 1, 2, 3].map(num => (
                              <div
                                key={num}
                                className={`px-4 py-3 cursor-pointer transition-colors ${childSeat === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                onClick={() => { setChildSeat(num); setShowChildDropdown(false); }}
                              >
                                {num}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Return Trip */}
                  <div className="border-t border-gray-200 pt-6 mb-6">
                    <label className="flex items-center gap-3 cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={addReturnTrip}
                        onChange={(e) => setAddReturnTrip(e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-[#36827f] focus:ring-[#36827f]"
                      />
                      <div className="flex items-center gap-2">
                        <ArrowLeftRight className="h-5 w-5 text-[#36827f]" />
                        <span className="font-medium text-gray-900">Return trip</span>
                        <span className="text-[#36827f] font-bold">€{basePrice}</span>
                      </div>
                    </label>

                    {addReturnTrip && (
                      <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                        {/* Return route info */}
                        <div className="text-sm text-gray-600 mb-4">
                          <span className="font-medium">{route.to_name}</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium">{route.from_name}</span>
                        </div>

                        {/* Return Date & Time */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Return Date */}
                          <div className="relative" data-field="returnDate" onClick={(e) => e.stopPropagation()}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Calendar className="h-4 w-4 inline mr-1 text-[#36827f]" /> Return Date
                            </label>
                            <button
                              type="button"
                              onClick={() => { setShowReturnCalendar(!showReturnCalendar); setShowReturnTimePicker(false); if (errors.returnDate) setErrors(prev => ({...prev, returnDate: null})); }}
                              className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showErrors && errors.returnDate ? 'border-red-500' : 'border-gray-200'}`}
                            >
                              <span className={!returnDate ? 'text-gray-400' : ''}>{formatDateDisplay(returnDate)}</span>
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showReturnCalendar ? 'rotate-180' : ''}`} />
                            </button>
                            {showErrors && errors.returnDate && (
                              <p className="text-red-500 text-sm mt-1">{errors.returnDate}</p>
                            )}
                            {showReturnCalendar && (
                              <div className="absolute top-full left-0 right-0 mt-2 z-30">
                                <CustomCalendar 
                                  value={returnDate}
                                  onChange={(date) => { setReturnDate(date); setErrors(prev => ({...prev, returnDate: null})); }}
                                  onClose={() => setShowReturnCalendar(false)}
                                  minDate={pickupDate || new Date()}
                                />
                              </div>
                            )}
                          </div>

                          {/* Return Time */}
                          <div className="relative" data-field="returnTime" onClick={(e) => e.stopPropagation()}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Clock className="h-4 w-4 inline mr-1 text-[#36827f]" /> Return Time
                            </label>
                            <button
                              type="button"
                              onClick={() => { setShowReturnTimePicker(!showReturnTimePicker); setShowReturnCalendar(false); if (errors.returnTime) setErrors(prev => ({...prev, returnTime: null})); }}
                              className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showErrors && errors.returnTime ? 'border-red-500' : 'border-gray-200'}`}
                            >
                              <span className={!returnTime ? 'text-gray-400' : ''}>{formatTimeDisplay(returnTime)}</span>
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showReturnTimePicker ? 'rotate-180' : ''}`} />
                            </button>
                            {showErrors && errors.returnTime && (
                              <p className="text-red-500 text-sm mt-1">{errors.returnTime}</p>
                            )}
                            {showReturnTimePicker && (
                              <div className="absolute top-full left-0 right-0 mt-2 z-30">
                                <CustomTimePicker 
                                  value={returnTime}
                                  onChange={(time) => { setReturnTime(time); setErrors(prev => ({...prev, returnTime: null})); }}
                                  onClose={() => setShowReturnTimePicker(false)}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Return Flight Number - only if return is from airport */}
                        {returnIsFromAirport && (
                          <div data-field="returnFlightNo">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Plane className="h-4 w-4 inline mr-1 text-[#36827f]" /> Return Flight Number
                            </label>
                            <input
                              type="text"
                              value={returnFlightNo}
                              onChange={(e) => { setReturnFlightNo(e.target.value); if (errors.returnFlightNo) setErrors(prev => ({...prev, returnFlightNo: null})); }}
                              placeholder="e.g., BA1234"
                              className={`w-full md:w-1/2 px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition ${showErrors && errors.returnFlightNo ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {showErrors && errors.returnFlightNo && (
                              <p className="text-red-500 text-sm mt-1">{errors.returnFlightNo}</p>
                            )}
                          </div>
                        )}

                        {/* Info note */}
                        <div className="flex items-start gap-2 text-sm text-gray-500 pt-2">
                          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>To book a return transfer for a different route, create a new booking</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Special Requests */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="h-4 w-4 inline mr-1 text-[#36827f]" /> Special Requests
                    </label>
                    <textarea
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      placeholder="Any special requirements..."
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Sidebar - Payment & Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  {/* Payment Option */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                        <CreditCard className="h-5 w-5 text-[#36827f]" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Payment Option</h3>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentType('full')}
                        data-testid="payment-full"
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          paymentType === 'full'
                            ? 'border-[#36827f] bg-[#36827f]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">Full Payment</div>
                        <div className="text-xl font-bold text-[#36827f] mt-1">€{totalPrice}</div>
                        <div className="text-xs text-gray-500 mt-1">Pay the full amount now</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentType('deposit')}
                        data-testid="payment-deposit"
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          paymentType === 'deposit'
                            ? 'border-[#36827f] bg-[#36827f]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-gray-900">25% Deposit</div>
                        <div className="text-xl font-bold text-[#36827f] mt-1">€{depositAmount}</div>
                        <div className="text-xs text-gray-500 mt-1">Pay the rest later</div>
                      </button>
                    </div>
                  </div>

                  {/* Booking Summary */}
                  <div className="bg-[#36827f]/5 rounded-2xl border border-[#36827f]/20 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-[#36827f]" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Booking Summary</h3>
                    </div>
                    
                    <div className="space-y-4 text-sm">
                      <div>
                        <span className="text-gray-500 block mb-1">Vehicle</span>
                        <span className="font-medium text-gray-900">{vehicle?.type || 'Select a vehicle'}</span>
                      </div>
                      
                      {/* Outbound Trip */}
                      <div className="pt-3 border-t border-gray-100">
                        <span className="text-gray-500 block mb-1">Outbound</span>
                        <div className="flex justify-between items-center">
                          <div className="font-medium text-gray-900">
                            {route.from_name} <span className="text-[#36827f]">→</span> {route.to_name}
                          </div>
                          <span className="font-medium text-gray-900">€{basePrice}</span>
                        </div>
                      </div>

                      {/* Return Trip */}
                      {addReturnTrip && (
                        <div className="pt-3 border-t border-gray-100">
                          <span className="text-gray-500 block mb-1">Return</span>
                          <div className="flex justify-between items-center">
                            <div className="font-medium text-gray-900">
                              {route.to_name} <span className="text-[#36827f]">→</span> {route.from_name}
                            </div>
                            <span className="font-medium text-gray-900">€{basePrice}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-3 border-t-2 border-gray-200 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Price</span>
                          <span className="font-bold text-gray-900">€{totalPrice}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900">Pay Now</span>
                          <span className="text-2xl font-bold text-[#36827f]">€{payNowAmount}</span>
                        </div>
                        {paymentType === 'deposit' && (
                          <div className="flex justify-between items-center text-gray-400 text-sm">
                            <span>Remaining (pay later)</span>
                            <span>€{totalPrice - depositAmount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {bookingError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                      {bookingError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="pay-now-btn"
                    className="w-full flex items-center justify-center gap-2 bg-[#36827f] hover:bg-[#2d6f6c] text-white py-4 text-lg font-bold rounded-xl shadow-lg shadow-[#36827f]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Redirecting to Payment...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5" />
                        Pay Now - €{payNowAmount}
                      </>
                    )}
                  </button>

                  {/* Security Notice */}
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Lock className="h-4 w-4" />
                      <span>Secure</span>
                    </div>
                    <span>•</span>
                    <span>Free cancellation 24h</span>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
